import { ZoomIn, ZoomOut } from "lucide-react";
import { motion } from "motion/react";
import { type Dispatch, type SetStateAction, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FitAllIcon, HandToolIcon, PointerToolIcon } from "./canvas-icons";
import type { CanvasTool } from "./canvas-utils";

interface CanvasZoomControlsProps {
	canvasTool: CanvasTool;
	setCanvasTool: Dispatch<SetStateAction<CanvasTool>>;
	zoomPercent: number;
	onZoomIn: () => void;
	onZoomOut: () => void;
	onResetView: () => void;
	onFitAll: () => void;
}

/** Bottom-right controls: hand/move tool, fit-all toggle, zoom in/out/reset. */
export function CanvasZoomControls({
	canvasTool,
	setCanvasTool,
	zoomPercent,
	onZoomIn: handleZoomIn,
	onZoomOut: handleZoomOut,
	onResetView: handleResetView,
	onFitAll: handleFitAll,
}: CanvasZoomControlsProps) {
	const [isFitAll, setIsFitAll] = useState(false);

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.3, delay: 0.5 }}
			className="absolute bottom-5 right-5 flex items-center gap-1.5 z-20"
			onMouseDown={(e) => e.stopPropagation()}
		>
			<button
				onClick={() =>
					setCanvasTool((t) => (t === "hand" ? "move" : "hand"))
				}
				className={cn(
					"p-1.5 rounded-full transition-colors cursor-pointer",
					canvasTool === "hand"
						? "bg-nav-active-bg text-text-primary"
						: "bg-bg-surface text-text-primary",
				)}
				title={
					canvasTool === "hand"
						? "Switch to Move tool"
						: "Switch to Hand tool"
				}
			>
				<div className="border border-border-subtle rounded-full p-1.5">
					{canvasTool === "hand" ? <HandToolIcon /> : <PointerToolIcon />}
				</div>
			</button>
			<button
				onClick={() => {
					if (isFitAll) {
						handleResetView();
						setIsFitAll(false);
					} else {
						handleFitAll();
						setIsFitAll(true);
					}
				}}
				title={isFitAll ? "Reset view" : "Fit all cards to view"}
				className={cn(
					"p-1.5 rounded-full transition-colors cursor-pointer",
					isFitAll
						? "bg-nav-active-bg text-text-primary"
						: "bg-bg-surface text-text-primary",
				)}
			>
				<div className="border border-border-subtle rounded-full p-1.5">
					<FitAllIcon />
				</div>
			</button>
			<div className="bg-bg-surface backdrop-blur-sm flex items-center border border-border-subtle rounded-lg">
				<Button
					variant="ghost"
					size="icon-lg"
					className="rounded-lg"
					onClick={handleZoomOut}
					title="Zoom out"
				>
					<ZoomOut size={16} />
				</Button>

				<Button
					variant="ghost"
					size="sm"
					className="min-w-[48px] px-2"
					onClick={handleResetView}
					title="Reset zoom"
				>
					{zoomPercent}%
				</Button>

				<Button
					variant="ghost"
					size="icon-lg"
					className="rounded-lg"
					onClick={handleZoomIn}
					title="Zoom in"
				>
					<ZoomIn size={16} />
				</Button>
			</div>
		</motion.div>
	);
}
